/**
 * S3 规则设置侧边栏(T43)
 *
 * 仅 settlement:rule:admin 角色可见:调阈值(规则 2 的出现率/窗口)。
 * 改完立即生效(下次扫描按新阈值);改动记审计(后端 last_modified_by/at)。
 *
 * 2026-07-06 规则精简:规则 3/4/5/6 退役(配置行已由迁移 159 删除),
 * 本侧边栏只剩规则 1/2 的文档与阈值。
 *
 * 2026-05-13 简化:
 *   - 砍掉"样本数下限"输入框(代码里固定 10 条)
 *   - 所有 label 改用业务大白话 + 单位(%、月)
 *   - 后端用 0~1 小数的字段(出现率)在前端以 % 显示并双向转换
 */

import React, { useCallback, useEffect, useState } from "react";
import {
    Drawer,
    Form,
    InputNumber,
    Switch,
    Button,
    Space,
    Typography,
    message,
    Divider,
    Tag,
    Alert,
    Empty,
    Spin,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import settlementApi from "../../../../api/settlementApi";

const { Title, Text, Paragraph } = Typography;

// 字段语义说明(2026-05-13 决策 8/9/10):
//   - "样本数下限"已砍掉(代码里固定 10 条,统计学常识)
//   - 字段 label 全部用业务大白话,带单位 + 默认值标注
//   - 凡是后端用 0~1 小数(出现频率)的,前端以 % 显示(unit: "percent"),
//     存进 form 时自动 ×100,提交时再 ÷100。业务永远只看 80%,不看 0.8
// 2026-07-06:规则 3/4/5/6 退役,文档条目一并移除;接口若返回未知规则,
// 走下方 render 处的兜底文案(title = rule_name)。
const RULE_DOCS = {
    rule1_fuel_missing: {
        title: "规则 1 — 燃油可能漏录",
        description: "做完账后,如果某航段燃油 = 0(很可能漏录),立即推到「数据异常」工作台(高级提醒,红色)。补录燃油后异常自动消失;选「忽略」即永久。",
        fields: [],
        reactivatable: false,
    },
    rule2_expected_missing: {
        title: "规则 2 — 该有的费用没出现",
        description:
            "如果某航段的某费用,历史上经常有(默认出现率 ≥ 80%),但本批次没有,就推到工作台(中级提醒,黄色)。补录后异常自动消失;选「忽略」即永久。\n对所有航线生效,包括只飞过 1~2 次的低频航线(80% 阈值天然过滤小样本噪声)。",
        fields: [
            { name: "min_present_ratio", label: "出现率阈值（默认 80%）", min: 0, max: 100, step: 5, suffix: "%", unit: "percent" },
            { name: "history_window_months", label: "历史窗口（默认 6 个月,看几个月内的数据)", min: 1, max: 36, step: 1, suffix: "月" },
        ],
        reactivatable: false,
    },
};

// 在 form 内部显示用 % 单位,存到后端是 0~1 小数。
// 用 Math.round 防浮点精度毛刺(0.8 * 100 = 80.00000001 之类)。
const toDisplay = (field, raw) => {
    if (raw === undefined || raw === null) return raw;
    if (field.unit === "percent" && typeof raw === "number") {
        return Math.round(raw * 100 * 100) / 100;
    }
    return raw;
};
const toStorage = (field, display) => {
    if (display === undefined || display === null) return display;
    if (field.unit === "percent" && typeof display === "number") {
        return Math.round(display * 100) / 10000;
    }
    return display;
};

const RuleConfigSidebar = ({ open, onClose, onSaved }) => {
    const [loading, setLoading] = useState(false);
    const [rules, setRules] = useState([]);
    const [savingRule, setSavingRule] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await settlementApi.dataQuality.listAnomalyRuleConfigs();
            setRules(res.data || []);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || err.message;
            message.error("加载规则配置失败: " + msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const handleSave = async (rule, values) => {
        setSavingRule(rule.rule_id);
        try {
            const cfg = RULE_DOCS[rule.rule_id];
            const thresholds = { ...rule.thresholds };
            for (const f of cfg.fields || []) {
                const display = values[`thr_${f.name}`];
                if (display !== undefined) {
                    thresholds[f.name] = toStorage(f, display);
                }
            }
            // 重激活语义:用 reactivate_threshold.enabled 承载"是否允许重激活"开关
            // 老阈值字段(amount_change_absolute / amount_change_percent)不再使用,
            // 但保留在 JSONB 里(非破坏性,后端忽略)。
            const payload = {
                enabled: values.enabled,
                thresholds,
            };
            if (cfg.reactivatable) {
                payload.reactivate_threshold = {
                    ...(rule.reactivate_threshold || {}),
                    enabled: values.reactivate_enabled !== false,
                };
            }
            await settlementApi.dataQuality.updateAnomalyRuleConfig(rule.rule_id, payload);
            message.success(`规则「${rule.rule_name}」已更新;下次扫描立即生效`);
            await load();
            if (onSaved) onSaved();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || err.message;
            message.error("保存失败: " + msg);
        } finally {
            setSavingRule(null);
        }
    };

    return (
        <Drawer
            title="规则设置(仅运营管理员)"
            open={open}
            onClose={onClose}
            width={600}
            destroyOnClose
        >
            <Spin spinning={loading}>
                {rules.length === 0 && !loading ? (
                    <Empty description="未加载到规则配置" />
                ) : (
                    rules.map((rule) => {
                        const doc = RULE_DOCS[rule.rule_id] || {
                            title: rule.rule_name,
                            description: "(无说明)",
                            fields: [],
                            reactivatable: false,
                        };
                        const initialValues = {
                            enabled: rule.enabled,
                            // 重激活开关:JSONB 里没记过就当 true(向后兼容)
                            reactivate_enabled: rule.reactivate_threshold?.enabled !== false,
                            ...Object.fromEntries(
                                (doc.fields || []).map((f) => [
                                    `thr_${f.name}`,
                                    toDisplay(f, rule.thresholds?.[f.name]),
                                ])
                            ),
                        };
                        return (
                            <div key={rule.rule_id} style={{ marginBottom: 24 }}>
                                <Title level={5} style={{ marginBottom: 4 }}>
                                    {doc.title}
                                    <Tag color={rule.enabled ? "green" : "default"} style={{ marginLeft: 8 }}>
                                        {rule.enabled ? "启用中" : "已停用"}
                                    </Tag>
                                </Title>
                                <Paragraph type="secondary" style={{ fontSize: 12 }}>
                                    {doc.description}
                                </Paragraph>
                                <Form
                                    layout="vertical"
                                    size="small"
                                    initialValues={initialValues}
                                    onFinish={(values) => handleSave(rule, values)}
                                >
                                    <Form.Item name="enabled" label="是否启用" valuePropName="checked">
                                        <Switch checkedChildren="启用" unCheckedChildren="停用" />
                                    </Form.Item>

                                    {(doc.fields || []).length > 0 && (
                                        <>
                                            <Divider plain style={{ margin: "8px 0" }}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    触发阈值
                                                </Text>
                                            </Divider>
                                            {doc.fields.map((f) => (
                                                <Form.Item
                                                    key={f.name}
                                                    name={`thr_${f.name}`}
                                                    label={f.label}
                                                >
                                                    <InputNumber
                                                        min={f.min}
                                                        max={f.max}
                                                        step={f.step}
                                                        style={{ width: "100%" }}
                                                        addonAfter={f.suffix}
                                                    />
                                                </Form.Item>
                                            ))}
                                        </>
                                    )}

                                    {doc.reactivatable && (
                                        <Form.Item
                                            name="reactivate_enabled"
                                            label="忽略后金额变化时重提醒"
                                            valuePropName="checked"
                                            tooltip="开启:已忽略的异常,后续金额发生任何变化都会重新激活提醒;关闭:忽略=永久,本规则不再骚扰"
                                            style={{ marginBottom: 8 }}
                                        >
                                            <Switch checkedChildren="开" unCheckedChildren="关" />
                                        </Form.Item>
                                    )}

                                    <Space>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<SaveOutlined />}
                                            loading={savingRule === rule.rule_id}
                                        >
                                            保存
                                        </Button>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            最后修改:
                                            {rule.last_modified_at
                                                ? rule.last_modified_at.replace("T", " ").slice(0, 16)
                                                : "—"}
                                        </Text>
                                    </Space>
                                </Form>
                                <Divider />
                            </div>
                        );
                    })
                )}
            </Spin>

            <Alert
                type="warning"
                showIcon
                message="阈值修改下次扫描立即生效;改动记审计"
                description="改完阈值后可点'立即扫描'按新规则重跑;若不熟悉阈值含义建议先和数据团队对齐再改。"
            />
        </Drawer>
    );
};

export default RuleConfigSidebar;
